import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { 
  FileText, 
  List, 
  Home, 
  ChevronLeft, 
  ChevronRight, 
  ChevronDown,
  PanelRight, 
  ListCheck,
  Wallet,
  Users,
  FileSpreadsheet,
  Shield,
  UserCheck,
  ShoppingCart,
  AlertCircle,
  BarChart3,
  CalendarDays,
  GraduationCap,
  CheckCircle,
  Calculator,
  TrendingUp,
  BatteryLow,
  UserSearch,
  BookOpen,
  DollarSign,
  Settings,
  Star,
  type LucideIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

interface MenuItem {
  title: string;
  url: string;
  icon: LucideIcon;
}

interface MenuGroup {
  label: string;
  icon: LucideIcon;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    label: "ภาพรวม",
    icon: Home,
    items: [
      { title: "หน้าหลัก", url: "/", icon: Home },
      { title: "สรุปข้อมูลรายเดือน", url: "/admin/monthly-snapshot", icon: BarChart3 },
      { title: "สรุปข้อมูลรายวัน", url: "/admin/daily-snapshot", icon: CalendarDays },
    ],
  },
  {
    label: "นักเรียน",
    icon: Users,
    items: [
      { title: "รายชื่อนักเรียนทั้งหมด", url: "/admin/students", icon: Users },
      { title: "รายงานพัฒนาการนักเรียน", url: "/admin", icon: FileText },
      { title: "นักเรียนใกล้หมดคอร์ส", url: "/admin/depletion-status", icon: BatteryLow },
      { title: "นักเรียนประจำ", url: "/admin/regular-students", icon: Users },
      { title: "ข้อมูลซื้อคอร์ส", url: "/admin/order-overview", icon: ShoppingCart },
    ],
  },
  {
    label: "ทดลองเรียน",
    icon: GraduationCap,
    items: [
      { title: "ทดลองเรียน", url: "/admin/class-trials", icon: GraduationCap },
      { title: "สถิติทดลองเรียน", url: "/admin/class-trials-stats", icon: TrendingUp },
    ],
  },
  {
    label: "ครู",
    icon: UserCheck,
    items: [
      { title: "ข้อมูลครู", url: "/admin/teachers", icon: UserCheck },
      { title: "แมทช์ครู", url: "/admin/teacher-match", icon: UserSearch },
      { title: "รายงานนักเรียน-ครู", url: "/admin/teacher-students", icon: Users },
      { title: "สรุปข้อมูลสถิติครู", url: "/admin/teacher-stats", icon: TrendingUp },
    ],
  },
  {
    label: "การเงิน",
    icon: DollarSign,
    items: [
      { title: "เงินเดือนครู", url: "/admin/teacher-salary", icon: Wallet },
      { title: "อนุมัติเงินเดือนครู", url: "/admin/teacher-payroll", icon: CheckCircle },
      { title: "ค่าคอมมิชชั่นครู", url: "/admin/commissions", icon: Calculator },
      { title: "ตั้งค่าระบบยืนยันเงินเดือน", url: "/admin/salary-verification", icon: Shield },
    ],
  },
  {
    label: "คลาสและชีท",
    icon: BookOpen,
    items: [
      { title: "ตารางประจำ", url: "/admin/class-templates", icon: CalendarDays },
      { title: "ตรวจสอบคลาสลากะทันหัน", url: "/admin/sudden-leave", icon: AlertCircle },
      { title: "ตรวจชีท", url: "/admin/worksheet-check", icon: ListCheck },
      { title: "จัดการชีทการสอน", url: "/admin/worksheet-management", icon: FileSpreadsheet },
    ],
  },
  {
    label: "รีวิว",
    icon: Star,
    items: [
      { title: "แบบประเมินความพึงพอใจผู้ปกครอง", url: "/admin/parent-reviews", icon: Star },
    ],
  },
  {
    label: "Events",
    icon: CalendarDays,
    items: [
      { title: "Events", url: "/admin/events", icon: CalendarDays },
      { title: "Event Balances", url: "/admin/student-event-balances", icon: Wallet },
    ],
  },
  {
    label: "ระบบ",
    icon: Settings,
    items: [
      { title: "Admin Logs", url: "/admin/logs", icon: List },
    ],
  },
];

export const AdminSidebar = () => {
  const { state, toggleSidebar } = useSidebar();
  const isCollapsed = state === "collapsed";
  const location = useLocation();

  const isItemActive = (item: MenuItem) => {
    if (item.url === "/" || item.url === "/admin") return location.pathname === item.url;
    return location.pathname === item.url || location.pathname.startsWith(item.url + "/");
  };

  const isGroupActive = (group: MenuGroup) =>
    group.items.some((item) => isItemActive(item));

  return (
    <Sidebar>
      <SidebarHeader className="pb-0">
        <div className="px-3 py-2">
          <h2 className="text-lg font-semibold tracking-tight">แผงควบคุมผู้ดูแลระบบ</h2>
          <p className="text-sm text-muted-foreground">Admin Dashboard</p>
        </div>
        <button
          onClick={toggleSidebar}
          className="absolute right-2 top-2 p-1 rounded-full hover:bg-muted"
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>เมนู</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuGroups.map((group) => (
                <Collapsible
                  key={group.label}
                  defaultOpen={isGroupActive(group)}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={group.label}
                        className={isGroupActive(group) ? "font-medium text-primary" : ""}
                      >
                        <group.icon className="h-5 w-5" />
                        <span>{group.label}</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {group.items.map((item) => {
                          const active = isItemActive(item);
                          return (
                            <SidebarMenuSubItem key={item.url}>
                              <SidebarMenuSubButton
                                asChild
                                className={active ? "bg-primary/10 text-primary font-medium border-l-2 border-primary" : ""}
                              >
                                <NavLink
                                  to={item.url}
                                  end={item.url === "/" || item.url === "/admin"}
                                >
                                  <item.icon className="h-4 w-4" />
                                  <span>{item.title}</span>
                                </NavLink>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="py-2">
        <div className="px-3 text-xs text-muted-foreground">
          {new Date().getFullYear()} © Admin Panel
        </div>
      </SidebarFooter>
      <SidebarRail />
      {isCollapsed && (
        <Button
          variant="secondary"
          size="icon"
          onClick={toggleSidebar}
          className="fixed top-4 left-[3.2rem] z-20 shadow-md border bg-background"
          aria-label="Expand sidebar"
        >
          <PanelRight className="h-4 w-4" />
        </Button>
      )}
    </Sidebar>
  );
};
